import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useEditorDrag } from "../../../canvas/dnd/useEditorDrag";
import { usePaletteItemActivate } from "../../../canvas/dnd/usePaletteItemActivate";
import type { DragData } from "../../../domain/dnd/types";
import { SNIPPET_CATALOG, type SnippetId } from "../../../domain/snippets";
import { insertSectionFromPalette } from "./sectionPaletteInsert";
import { SectionPalettePreview } from "./paletteGlyphs";
import "components/builderShell/builderToolbarMenuView.scss";
import "./emailBuilderAddElementsPanel.scss";

interface IEmailBuilderSectionPaletteProps {
  onAfterInsert?: () => void;
}

export function EmailBuilderSectionPalette(props: IEmailBuilderSectionPaletteProps) {
  const { onAfterInsert } = props;
  const { t } = useTranslation();

  return (
    <div className="builder-toolbar-palette-grid">
      {SNIPPET_CATALOG.map(snippet => (
        <SectionPaletteItem
          key={snippet.id}
          snippetId={snippet.id}
          label={t(snippet.label)}
          description={t(snippet.description)}
          onInsert={() => {
            insertSectionFromPalette(snippet.id);
            onAfterInsert?.();
          }}
        />
      ))}
    </div>
  );
}

function SectionPaletteItem({
  snippetId,
  label,
  description,
  onInsert,
}: {
  snippetId: SnippetId;
  label: string;
  description: string;
  onInsert: () => void;
}) {
  const dragData: DragData = { kind: "palette-section", snippetId };
  const { dragRef, isDragging } = useEditorDrag(dragData, {
    id: `palette-section:${snippetId}`,
  });
  const handleActivate = usePaletteItemActivate(onInsert, isDragging);

  return (
    <button
      type="button"
      ref={dragRef}
      aria-label={label}
      title={description}
      className={classNames("builder-toolbar-palette-item", "email-builder-section-palette-item", {
        "builder-toolbar-palette-item--dragging": isDragging,
      })}
      style={{ opacity: isDragging ? 0.45 : 1 }}
      onClick={handleActivate}
    >
      <SectionPalettePreview snippetId={snippetId} />
      <span className="builder-toolbar-palette-item-title">{label}</span>
    </button>
  );
}
